import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { 
  Plus, FileText, Send, Download, Eye, Edit, Trash2, 
  DollarSign, Calendar, User, Mail, Phone, MapPin,
  Search, Filter, X, CheckCircle, Clock, AlertCircle, XCircle,
  Save, Hash, RefreshCw, Settings, Printer, Link2, Copy, Check, Package
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

// Stripe will be loaded dynamically when needed
let stripePromise = null;

const getStripePromise = async () => {
  if (!stripePromise) {
    try {
      const getBackendURL = () => {
        if (process.env.REACT_APP_BACKEND_URL) return process.env.REACT_APP_BACKEND_URL;
        const origin = window.location.origin;
        if (origin.includes('localhost:3000')) return 'http://localhost:8001';
        return origin + '/pandvah-backend';
      };
      const backendUrl = getBackendURL();
      const response = await axios.get(`${backendUrl}/api/stripe/publishable-key`);
      if (response.data.success) {
        stripePromise = loadStripe(response.data.publishable_key);
      }
    } catch (error) {
      console.error('Error loading Stripe key:', error);
    }
  }
  return stripePromise;
};

// Stripe Payment Form Component
const PaymentForm = ({ clientSecret, amount, invoiceId, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  
  const getBackendURL = () => {
    if (process.env.REACT_APP_BACKEND_URL) return process.env.REACT_APP_BACKEND_URL;
    const origin = window.location.origin;
    if (origin.includes('localhost:3000')) return 'http://localhost:8001';
    return origin + '/pandvah-backend';
  };
  const backendUrl = getBackendURL();

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const cardElement = elements.getElement(CardElement);

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (error) {
        setError(error.message);
        setProcessing(false);
      } else if (paymentIntent.status === 'succeeded') {
        // Notify backend that payment succeeded
        await axios.post(`${backendUrl}/api/invoices/${invoiceId}/confirm-payment`, {
          payment_intent_id: paymentIntent.id
        });
        
        setProcessing(false);
        onSuccess();
      }
    } catch (err) {
      setError(err.message || 'Payment failed');
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="bg-gray-50 p-4 rounded-lg">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Details
        </label>
        <div className="bg-white p-3 rounded border border-gray-300">
          <CardElement
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
                invalid: {
                  color: '#9e2146',
                },
              },
            }}
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 px-4 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium"
          disabled={processing}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          className="flex-1 px-4 py-3 text-white rounded-md hover:opacity-90 font-medium disabled:bg-gray-400"
          style={{ backgroundColor: !stripe || processing ? '#9ca3af' : 'var(--theme-primary)' }}
        >
          {processing ? 'Processing...' : `Pay $${amount.toFixed(2)}`}
        </button>
      </div>
    </form>
  );
};

const Invoices = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const viewInvoiceId = queryParams.get('view');
  const isPublicView = !!viewInvoiceId;
  const { user } = useAuth();
  
  const [activeTab, setActiveTab] = useState(isPublicView ? 'list' : 'generate');
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const itemsPerPage = 10;

  
  // Form state for invoice generation
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [lineItems, setLineItems] = useState([]);
  const [showLineItemOptions, setShowLineItemOptions] = useState(false);
  const [taxEnabled, setTaxEnabled] = useState(false);
  const [taxRate, setTaxRate] = useState(0);
  const [dueDateEnabled, setDueDateEnabled] = useState(false);
  const [dueDate, setDueDate] = useState('');
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState('Payment is due within 30 days of invoice date.');
  const [sendViaEmail, setSendViaEmail] = useState(false);
  const [sendViaSMS, setSendViaSMS] = useState(false);
  const [manualShare, setManualShare] = useState(false);
  const [enabledGateways, setEnabledGateways] = useState([]);
  const [paymentGateway, setPaymentGateway] = useState('none');
  
  // Invoice creation success modal
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [createdInvoiceData, setCreatedInvoiceData] = useState(null);
  
  // Validation error modal
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  
  // Public invoice view
  const [viewingInvoice, setViewingInvoice] = useState(null);
  const [showInvoiceViewModal, setShowInvoiceViewModal] = useState(false);
  
  // Payment link modal
  const [showPaymentLinkModal, setShowPaymentLinkModal] = useState(false);
  const [selectedPaymentLink, setSelectedPaymentLink] = useState(null);
  const [selectedInvoiceNumber, setSelectedInvoiceNumber] = useState(null);
  
  // Share invoice link modal
  const [showShareLinkModal, setShowShareLinkModal] = useState(false);
  const [shareInvoiceLink, setShareInvoiceLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Stripe inline payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentClientSecret, setPaymentClientSecret] = useState(null);
  
  // Payment confirmation modal
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  const [paidInvoiceDetails, setPaidInvoiceDetails] = useState(null);
  
  // Validation error modal
  const [showValidationError, setShowValidationError] = useState(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState('');
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentInvoiceId, setPaymentInvoiceId] = useState(null);
  const [stripeReady, setStripeReady] = useState(null);
  
  // Business info for invoice display
  const [businessInfo, setBusinessInfo] = useState(null);
  
  // Invoice Settings state
  const [invoicePrefix, setInvoicePrefix] = useState('INV-');
  const [invoiceStartingNumber, setInvoiceStartingNumber] = useState(1000);
  const [invoiceCurrentNumber, setInvoiceCurrentNumber] = useState(1000);
  const [billFromTemplate, setBillFromTemplate] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Delete and Resend state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);
  const [invoiceToResend, setInvoiceToResend] = useState(null);
  const [resending, setResending] = useState(false);

  
  // Load Stripe when component mounts
  useEffect(() => {
    getStripePromise().then(stripe => setStripeReady(stripe));
  }, []);
  
  // Fetch business info on component mount
  useEffect(() => {
    fetchBusinessInfo();
  }, []);
  
  // Customer search functionality
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [searchedCustomers, setSearchedCustomers] = useState([]);
  const [isSearchingCustomers, setIsSearchingCustomers] = useState(false);
  
  // Get backend URL with the same logic as other components
  const getBackendURL = () => {
    if (process.env.REACT_APP_BACKEND_URL) {
      return process.env.REACT_APP_BACKEND_URL;
    }
    
    const origin = window.location.origin;
    if (origin.includes('walrus-app') || origin.includes('ondigitalocean.app')) {
      return origin + '/pandvah-backend';
    }
    
    if (origin.includes('localhost:3000')) {
      return 'http://localhost:8001';
    }
    
    // For production domains, append /pandvah-backend
    return origin + '/pandvah-backend';
  };
  
  const backendUrl = getBackendURL();
  
  const fetchBusinessInfo = async () => {
    try {
      const response = await axios.get(`${backendUrl}/api/business-info`);
      const data = response.data;
      setBusinessInfo(data);
      
      // Also set invoice settings state
      setInvoicePrefix(data.invoice_prefix || 'INV-');
      setInvoiceStartingNumber(data.invoice_starting_number || 1000);
      setInvoiceCurrentNumber(data.invoice_current_number || data.invoice_starting_number || 1000);
      setBillFromTemplate(data.bill_from_template || '{BUSINESS_NAME}\n{BUSINESS_STREET_ADDRESS}\n{BUSINESS_CITY_STATE_ZIP}\n{BUSINESS_PHONE}');
      setPaymentGateway(data.default_payment_gateway || 'none');
    } catch (error) {
      console.error('Error fetching business info:', error);
    }
  };

  const renderBillFrom = () => {
    if (!businessInfo) return 'Loading...';
    
    // Get template or use default
    const template = businessInfo.bill_from_template || '{BUSINESS_NAME}\n{BUSINESS_STREET_ADDRESS}\n{BUSINESS_CITY_STATE_ZIP}\n{BUSINESS_PHONE}';
    
    // Format city, state, zip (combine available fields)
    let cityStateZip = '';
    if (businessInfo.city && businessInfo.zip_code) {
      cityStateZip = `${businessInfo.city}, ${businessInfo.zip_code}`;
    } else if (businessInfo.city) {
      cityStateZip = businessInfo.city;
    } else if (businessInfo.zip_code) {
      cityStateZip = businessInfo.zip_code;
    }
    
    // Replace placeholders with actual values
    const rendered = template
      .replace(/{BUSINESS_NAME}/g, businessInfo.hospital_name || 'Business Name')
      .replace(/{BUSINESS_STREET_ADDRESS}/g, businessInfo.street_address || businessInfo.address || 'Street Address')
      .replace(/{BUSINESS_CITY_STATE_ZIP}/g, cityStateZip || 'City, State ZIP')
      .replace(/{BUSINESS_ADDRESS}/g, businessInfo.address || 'Business Address')  // Keep for backward compatibility
      .replace(/{BUSINESS_PHONE}/g, businessInfo.phone || 'Business Phone')
      .replace(/{BUSINESS_EMAIL}/g, businessInfo.email || 'Business Email');
    
    return rendered;
  };


  // Fetch invoice for public viewing
  useEffect(() => {
    if (viewInvoiceId) {
      fetchInvoiceForPublicView(viewInvoiceId);
    }
  }, [viewInvoiceId]);

  // Fetch invoices when switching to list tab or when filters/page changes
  useEffect(() => {
    if (activeTab === 'list') {
      fetchInvoices(currentPage);
    }
  }, [activeTab, statusFilter]);

  // Reset to page 1 when status filter changes
  useEffect(() => {
    if (activeTab === 'list') {
      setCurrentPage(1);
      fetchInvoices(1);
    }
  }, [statusFilter]);


  const fetchInvoiceForPublicView = async (invoiceId) => {
    try {
      const response = await axios.get(`${backendUrl}/api/invoices/${invoiceId}/public`);
      if (response.data.success) {
        setViewingInvoice(response.data.invoice);
        setShowInvoiceViewModal(true);
      }
    } catch (error) {
      console.error('Error fetching invoice for public view:', error);
      showNotification('Failed to load invoice', 'error');
    }
  };

  useEffect(() => {
    if (activeTab === 'list') {
      fetchInvoices();
    } else {
      fetchCustomers();
      fetchEnabledGateways();
    }
  }, [activeTab]);

  const fetchEnabledGateways = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/payment-gateways`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // Filter only enabled gateways
        const enabled = response.data.gateways.filter(gw => gw.enabled === true);
        setEnabledGateways(enabled);
      }
    } catch (error) {
      console.error('Error fetching payment gateways:', error);
      setEnabledGateways([]);
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setCustomers(response.data.customers);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
      showNotification('Failed to load customers', 'error');
    }
  };

  const fetchInvoices = async (page = 1) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const skip = (page - 1) * itemsPerPage;
      const statusParam = statusFilter !== 'all' ? `&status=${statusFilter}` : '';
      const response = await axios.get(`${backendUrl}/api/invoices?skip=${skip}&limit=${itemsPerPage}${statusParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        console.log('Fetched invoices:', response.data.invoices);
        setInvoices(response.data.invoices);
        setTotalInvoices(response.data.total || 0);
        setTotalPages(Math.ceil((response.data.total || 0) / itemsPerPage));
        setCurrentPage(page);
        
        // Scroll to top of page
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
      setTotalPages(1);
      setTotalInvoices(0);
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 4000);
  };

  // Invoice Settings Handlers
  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      
      await axios.put(`${backendUrl}/api/business-info`, {
        invoice_prefix: invoicePrefix,
        invoice_starting_number: invoiceStartingNumber,
        invoice_current_number: invoiceCurrentNumber,
        bill_from_template: billFromTemplate,
        default_payment_gateway: paymentGateway
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Refresh business info
      await fetchBusinessInfo();
      
      showNotification('Invoice settings saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving settings:', error);
      showNotification('Failed to save invoice settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleResetCounter = async () => {
    try {
      setResetting(true);
      
      const response = await axios.post(`${backendUrl}/api/invoice-settings/reset-counter`, {}, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.data.success) {
        setInvoiceCurrentNumber(response.data.current_number);
        showNotification(response.data.message, 'success');
        setShowResetModal(false);
      }
    } catch (error) {
      console.error('Error resetting counter:', error);
      showNotification('Failed to reset invoice counter', 'error');
    } finally {
      setResetting(false);
    }
  };

  const insertPlaceholder = (placeholder) => {
    setBillFromTemplate(prev => prev + `{${placeholder}}`);
  };

  const placeholders = [
    { key: 'BUSINESS_NAME', label: 'Business Name' },
    { key: 'BUSINESS_STREET_ADDRESS', label: 'Street Address' },
    { key: 'BUSINESS_CITY_STATE_ZIP', label: 'City, State ZIP' },
    { key: 'BUSINESS_PHONE', label: 'Phone Number' },
    { key: 'BUSINESS_EMAIL', label: 'Email Address' }
  ];

  // Delete invoice handler
  const handleDeleteInvoice = async () => {
    try {
      setDeleting(true);
      await axios.delete(`${backendUrl}/api/invoices/${invoiceToDelete._id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      showNotification('Invoice deleted successfully', 'success');
      setShowDeleteModal(false);
      setInvoiceToDelete(null);
      fetchInvoices(); // Refresh the list
    } catch (error) {
      console.error('Error deleting invoice:', error);
      showNotification('Failed to delete invoice', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // Resend invoice handler
  const handleResendInvoice = async (method) => {
    try {
      setResending(true);
      await axios.post(`${backendUrl}/api/invoices/${invoiceToResend._id}/resend`, 
        { method }, // 'email' or 'sms'
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      showNotification(`Invoice sent via ${method.toUpperCase()} successfully`, 'success');
      setShowResendModal(false);
      setInvoiceToResend(null);
    } catch (error) {
      console.error('Error resending invoice:', error);
      showNotification('Failed to resend invoice', 'error');
    } finally {
      setResending(false);
    }
  };



  // Customer search function with debouncing
  const searchCustomers = async (searchTerm) => {
    if (searchTerm.length < 3) {
      setSearchedCustomers([]);
      return;
    }

    setIsSearchingCustomers(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/customers?search=${encodeURIComponent(searchTerm)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Customer search response:', response.data);
      
      // The API returns customers array directly (no success field in this endpoint)
      if (response.data && response.data.customers) {
        const customers = response.data.customers || [];
        console.log('Found customers:', customers);
        setSearchedCustomers(customers);
      } else {
        console.log('No customers data in response');
        setSearchedCustomers([]);
      }
    } catch (error) {
      console.error('Error searching customers:', error);
      setSearchedCustomers([]);
    } finally {
      setIsSearchingCustomers(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (customerSearchTerm) {
        searchCustomers(customerSearchTerm);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(delayedSearch);
  }, [customerSearchTerm]);

  const addLineItem = (withQuantity) => {
    const newItem = {
      id: Date.now(),
      description: '',
      hasQuantity: withQuantity,
      quantity: withQuantity ? 1 : null,
      unit_price: 0,
      amount: 0
    };
    setLineItems([...lineItems, newItem]);
    setShowLineItemOptions(false);
  };

  const removeLineItem = (id) => {
    setLineItems(lineItems.filter(item => item.id !== id));
  };

  const updateLineItem = (id, field, value) => {
    setLineItems(lineItems.map(item => {
      if (item.id === id) {
        const updated = { ...item, [field]: value };
        if (item.hasQuantity && (field === 'quantity' || field === 'unit_price')) {
          updated.amount = parseFloat(updated.quantity || 0) * parseFloat(updated.unit_price || 0);
        } else if (!item.hasQuantity && field === 'unit_price') {
          updated.amount = parseFloat(updated.unit_price || 0);
        }
        return updated;
      }
      return item;
    }));
  };

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  const calculateTax = () => {
    return calculateSubtotal() * (taxRate / 100);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateTax();
  };

  const handleGenerateInvoice = async () => {
    if (!selectedCustomer) {
      showValidationErrorModal('Please select a customer before generating the invoice.');
      return;
    }

    if (lineItems.length === 0) {
      showValidationErrorModal('Please add at least one line item to the invoice.');
      return;
    }

    // Validate line items
    const invalidItem = lineItems.find(item => {
      if (!item.description || item.unit_price <= 0) return true;
      if (item.hasQuantity && item.quantity <= 0) return true;
      return false;
    });

    if (invalidItem) {
      showValidationErrorModal('Please fill in all line item details correctly. Each item must have a description, valid price, and quantity (if applicable).');
      return;
    }

    if (dueDateEnabled && !dueDate) {
      showValidationErrorModal('Please select a due date or disable the due date option.');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Get customer ID - handle both _id and id formats
      const customerId = selectedCustomer.id || selectedCustomer._id;
      
      if (!customerId) {
        showNotification('Invalid customer selected. Please try again.', 'error');
        setLoading(false);
        return;
      }
      
      const invoiceData = {
        customer_id: customerId,
        customer_name: selectedCustomer.name || `${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}`.trim(),
        customer_email: selectedCustomer.email || '',
        customer_phone: selectedCustomer.phone || '',
        customer_address: selectedCustomer.address || '',
        line_items: lineItems.map(item => ({
          description: item.description,
          quantity: item.hasQuantity ? parseInt(item.quantity) : 1,
          unit_price: parseFloat(item.unit_price),
          amount: parseFloat(item.amount)
        })),
        tax_rate: taxEnabled ? parseFloat(taxRate) : 0,
        tax_amount: taxEnabled ? calculateTax() : 0,
        subtotal: calculateSubtotal(),
        total: calculateTotal(),
        due_date: dueDateEnabled ? new Date(dueDate).toISOString() : null,
        notes: notes,
        terms: terms,
        payment_gateway: paymentGateway,
        send_via_email: sendViaEmail,
        send_via_sms: sendViaSMS,
        manual_share: manualShare
      };

      console.log('Sending invoice data:', invoiceData);
      console.log('Selected customer object:', selectedCustomer);

      const response = await axios.post(`${backendUrl}/api/invoices`, invoiceData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Store created invoice data
        setCreatedInvoiceData({
          invoice_number: response.data.invoice_number,
          invoice_id: response.data.invoice_id,
          payment_link: response.data.payment_link,
          manual_share_link: response.data.manual_share_link,
          status: response.data.status
        });
        
        // Show success modal
        setShowSuccessModal(true);
        
        showNotification('Invoice created successfully!', 'success');
        
        // Reset form
        setSelectedCustomer(null);
        setCustomerSearchTerm('');
        setSearchedCustomers([]);
        setLineItems([{ id: Date.now(), description: '', quantity: 1, unit_price: 0, amount: 0 }]);
        setTaxRate(0);
        setDueDate('');
        setNotes('');
        setSendViaEmail(false);
        setSendViaSMS(false);
        setManualShare(false);
        setPaymentGateway('none');
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      
      // Check if it's a validation error (422)
      if (error.response?.status === 422 && error.response?.data) {
        const errorData = error.response.data;
        
        // Parse validation errors
        let errors = [];
        
        if (errorData.errors && Array.isArray(errorData.errors)) {
          // Detailed validation errors from backend
          errors = errorData.errors.map(err => ({
            field: err.field || 'Unknown field',
            message: err.message || 'Validation error'
          }));
        } else if (errorData.detail) {
          // Simple error message
          errors = [{ field: 'General', message: errorData.detail }];
        } else if (errorData.message) {
          errors = [{ field: 'General', message: errorData.message }];
        } else {
          errors = [{ field: 'General', message: 'Validation failed. Please check all fields.' }];
        }
        
        setValidationErrors(errors);
        setShowValidationModal(true);
      } else {
        // Other errors - show toast
        showNotification(error.response?.data?.detail || 'Failed to create invoice', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      showNotification(`${label} copied to clipboard!`, 'success');
    } catch (error) {
      console.error('Failed to copy:', error);
      showNotification('Failed to copy to clipboard', 'error');
    }
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
    setCreatedInvoiceData(null);
    setActiveTab('list');
  };

  const closeValidationModal = () => {
    setShowValidationModal(false);
    setValidationErrors([]);
  };

  const closeInvoiceViewModal = () => {
    setShowInvoiceViewModal(false);
    setViewingInvoice(null);
    // Redirect to home page
    navigate('/');
  };

  const showValidationErrorModal = (message) => {
    setValidationErrorMessage(message);
    setShowValidationError(true);
  };

  const showPaymentLinkPopup = (paymentLink, invoiceNumber, manualShareLink) => {
    setSelectedPaymentLink(paymentLink);
    setSelectedInvoiceNumber(invoiceNumber);
    setShowPaymentLinkModal(true);
  };

  const closePaymentLinkModal = () => {
    setShowPaymentLinkModal(false);
    setSelectedPaymentLink(null);
    setSelectedInvoiceNumber(null);
  };

  const regeneratePaymentLink = async (invoiceId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${backendUrl}/api/invoices/${invoiceId}/regenerate-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        showNotification('Payment link generated successfully!', 'success');
        
        // Update the viewing invoice with new links
        setViewingInvoice(prev => ({
          ...prev,
          payment_link: response.data.payment_link,
          manual_share_link: response.data.manual_share_link
        }));
        
        // Refresh invoice list
        fetchInvoices();
      }
    } catch (error) {
      console.error('Error regenerating payment link:', error);
      showNotification('Failed to generate payment link', 'error');
    }
  };

  const openPaymentModal = (invoice) => {
    if (invoice.stripe_client_secret) {
      setPaymentClientSecret(invoice.stripe_client_secret);
      setPaymentAmount(invoice.total);
      setPaymentInvoiceId(invoice._id);
      setShowPaymentModal(true);
    } else {
      showNotification('Payment information not available', 'error');
    }
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentClientSecret(null);
    setPaymentAmount(0);
    setPaymentInvoiceId(null);
  };

  const handlePrintInvoice = () => {
    window.print();
  };


  const handlePaymentSuccess = () => {
    closePaymentModal();
    
    // Update the viewing invoice status with paid_at timestamp
    const updatedInvoice = {
      ...viewingInvoice,
      payment_status: 'completed',
      status: 'paid',
      paid_at: new Date().toISOString()
    };
    setViewingInvoice(updatedInvoice);
    
    // Store invoice details for confirmation modal
    setPaidInvoiceDetails(updatedInvoice);
    
    // Close invoice view modal first
    setShowInvoiceViewModal(false);
    
    // Show payment confirmation modal
    setShowPaymentConfirmation(true);
    
    // Refresh invoice list
    fetchInvoices();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { icon: Edit, color: 'bg-gray-100 text-gray-800', label: 'Manual Send' },
      sent: { icon: Send, color: 'bg-blue-100 text-blue-800', label: 'Sent' },
      paid: { icon: CheckCircle, color: 'bg-green-100 text-green-800', label: 'Paid' },
      overdue: { icon: AlertCircle, color: 'bg-red-100 text-red-800', label: 'Overdue' },
      cancelled: { icon: XCircle, color: 'bg-gray-100 text-gray-800', label: 'Cancelled' }
    };

    const config = statusConfig[status] || statusConfig.draft;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const filteredInvoices = invoices.filter(invoice =>
    invoice.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    invoice.customer_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        {/* Notification */}
        {notification.show && (
          <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
            notification.type === 'success' ? 'bg-green-500' : 'bg-red-500'
          } text-white`}>
            {notification.message}
          </div>
        )}

        {/* Show normal invoice page only if not in public view mode */}
        {!isPublicView && (
          <>
            {/* Header */}
            <div className="bg-white rounded-xl shadow-sm mb-6">
              <div className="p-4 border-b border-gray-200">
                <h1 className="text-lg font-semibold text-gray-900">Invoices</h1>
              </div>

              {/* Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              <button
                onClick={() => setActiveTab('generate')}
                className={`py-3 px-6 text-sm font-medium border-b-2 ${
                  activeTab === 'generate'
                    ? 'border-theme-blue text-theme-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Plus className="h-4 w-4 inline mr-2" />
                Generate Invoice
              </button>
              <button
                onClick={() => setActiveTab('list')}
                className={`py-3 px-6 text-sm font-medium border-b-2 ${
                  activeTab === 'list'
                    ? 'border-theme-blue text-theme-blue'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FileText className="h-4 w-4 inline mr-2" />
                Invoice List
              </button>
              {/* Settings Tab - Only for Admin/Owner */}
              {user && (user.role === 'admin' || user.role === 'owner') && (
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`py-3 px-6 text-sm font-medium border-b-2 ${
                    activeTab === 'settings'
                      ? 'border-theme-blue text-theme-blue'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Settings className="h-4 w-4 inline mr-2" />
                  Invoice Settings
                </button>
              )}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'generate' ? (
          // Generate Invoice Tab
          <div className="bg-white rounded-xl shadow-sm p-6">
            {/* Customer Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Search className="h-4 w-4 inline mr-2" />
                Search Customer *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={customerSearchTerm}
                  onChange={(e) => setCustomerSearchTerm(e.target.value)}
                  placeholder="Type at least 3 characters to search by customer name..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent pl-10 text-sm"
                />
                <Search className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                {isSearchingCustomers && (
                  <div className="absolute right-3 top-3.5">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-theme-blue"></div>
                  </div>
                )}
              </div>

              {/* Search Results */}
              {searchedCustomers.length > 0 && customerSearchTerm.length >= 3 && (
                <div className="mt-2 border border-gray-200 rounded-lg bg-white shadow-sm">
                  <div className="px-3 py-2 bg-gray-50 border-b text-xs font-medium text-gray-700">Search Results</div>
                  <div className="max-h-60 overflow-y-auto">
                    {searchedCustomers.map((customer) => (
                      <div
                        key={customer._id || customer.id}
                        onClick={() => {
                          console.log('Selected customer:', customer);
                          setSelectedCustomer(customer);
                          setCustomerSearchTerm('');
                          setSearchedCustomers([]);
                        }}
                        className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{customer.name}</div>
                            <div className="text-sm text-gray-500">{customer.email}</div>
                            {customer.phone && (
                              <div className="text-sm text-gray-500">{customer.phone}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {selectedCustomer && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center">
                    <User className="h-4 w-4 text-theme-blue mr-2" />
                    <span className="text-gray-700">{selectedCustomer.name}</span>
                  </div>
                  <div className="flex items-center">
                    <Mail className="h-4 w-4 text-theme-blue mr-2" />
                    <span className="text-gray-700">{selectedCustomer.email}</span>
                  </div>
                  {selectedCustomer.phone && (
                    <div className="flex items-center">
                      <Phone className="h-4 w-4 text-theme-blue mr-2" />
                      <span className="text-gray-700">{selectedCustomer.phone}</span>
                    </div>
                  )}
                  {selectedCustomer.address && (
                    <div className="flex items-center">
                      <MapPin className="h-4 w-4 text-theme-blue mr-2" />
                      <span className="text-gray-700">{selectedCustomer.address}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Line Items */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  Line Items *
                </label>
                <button
                  onClick={() => setShowLineItemOptions(!showLineItemOptions)}
                  className="px-4 py-2 bg-theme-blue text-white text-sm font-medium rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Line Item
                </button>
              </div>

              {/* Line Item Type Selection */}
              {showLineItemOptions && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-gray-700 mb-3 font-medium">Select line item type:</p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => addLineItem(true)}
                      className="flex-1 px-4 py-3 bg-white border-2 border-theme-blue text-theme-blue rounded-lg hover:bg-blue-50 font-medium text-sm flex items-center justify-center gap-2"
                    >
                      <Package className="h-4 w-4" />
                      With Quantity
                    </button>
                    <button
                      onClick={() => addLineItem(false)}
                      className="flex-1 px-4 py-3 bg-white border-2 border-theme-blue text-theme-blue rounded-lg hover:bg-blue-50 font-medium text-sm flex items-center justify-center gap-2"
                    >
                      <FileText className="h-4 w-4" />
                      Without Quantity
                    </button>
                  </div>
                </div>
              )}

              {/* Line Items List */}
              {lineItems.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <FileText className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">No line items added yet</p>
                  <p className="text-xs text-gray-500 mt-1">Click "Add Line Item" to get started</p>
                </div>
              ) : (
                <div className="border border-gray-300 rounded-lg overflow-hidden">
                  {lineItems.map((item, index) => (
                    <div key={item.id}>
                      {/* Header Row - Show only for first item */}
                      {index === 0 && (
                        <div className={`grid ${item.hasQuantity ? 'grid-cols-12' : 'grid-cols-10'} gap-3 px-3 py-2 bg-gray-100 border-b border-gray-300`}>
                          <div className={item.hasQuantity ? 'col-span-5' : 'col-span-7'}>
                            <span className="text-xs font-medium text-gray-700">Description</span>
                          </div>
                          {item.hasQuantity && (
                            <div className="col-span-2">
                              <span className="text-xs font-medium text-gray-700">Quantity</span>
                            </div>
                          )}
                          <div className="col-span-2">
                            <span className="text-xs font-medium text-gray-700">{item.hasQuantity ? 'Unit Price' : 'Amount'}</span>
                          </div>
                          {item.hasQuantity && (
                            <div className="col-span-2">
                              <span className="text-xs font-medium text-gray-700">Total</span>
                            </div>
                          )}
                          <div className="col-span-1">
                            <span className="text-xs font-medium text-gray-700">Action</span>
                          </div>
                        </div>
                      )}
                      
                      {/* Data Row */}
                      <div className={`grid ${item.hasQuantity ? 'grid-cols-12' : 'grid-cols-10'} gap-3 px-3 py-3 ${index !== lineItems.length - 1 ? 'border-b border-gray-200' : ''} hover:bg-gray-50`}>
                        <div className={item.hasQuantity ? 'col-span-5' : 'col-span-7'}>
                          <input
                            type="text"
                            placeholder="Enter description"
                            value={item.description}
                            onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                        {item.hasQuantity && (
                          <div className="col-span-2">
                            <input
                              type="number"
                              placeholder="Qty"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateLineItem(item.id, 'quantity', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            />
                          </div>
                        )}
                        <div className="col-span-2">
                          <input
                            type="number"
                            placeholder="0.00"
                            min="0"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(e) => updateLineItem(item.id, 'unit_price', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                        {item.hasQuantity && (
                          <div className="col-span-2">
                            <input
                              type="text"
                              value={`$${item.amount.toFixed(2)}`}
                              readOnly
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-700 font-medium"
                            />
                          </div>
                        )}
                        <div className="col-span-1 flex justify-center items-center">
                          <button
                            onClick={() => removeLineItem(item.id)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg"
                            title="Remove item"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Tax Section */}
            <div className="mb-6">
              {!taxEnabled ? (
                <button
                  onClick={() => setTaxEnabled(true)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 flex items-center gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Tax
                </button>
              ) : (
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700">Tax Rate</label>
                    <button
                      onClick={() => {
                        setTaxEnabled(false);
                        setTaxRate(0);
                      }}
                      className="text-xs text-red-600 hover:text-red-700 font-medium"
                    >
                      Remove Tax
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      placeholder="0"
                      min="0"
                      max="100"
                      step="0.1"
                      value={taxRate}
                      onChange={(e) => setTaxRate(e.target.value)}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                    <span className="text-sm text-gray-600">%</span>
                    <span className="ml-auto text-sm font-medium text-gray-900">
                      Tax Amount: ${calculateTax().toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Calculation Summary */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg border-2 border-gray-300">
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium text-gray-900">${calculateSubtotal().toFixed(2)}</span>
                </div>
                {taxEnabled && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Tax ({taxRate}%):</span>
                    <span className="font-medium text-gray-900">${calculateTax().toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-base pt-2 border-t border-gray-300">
                  <span className="font-semibold text-gray-900">Total:</span>
                  <span className="font-bold text-theme-blue text-lg">${calculateTotal().toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Due Date */}
            <div className="mb-6">
              {!dueDateEnabled ? (
                <button
                  onClick={() => setDueDateEnabled(true)}
                  className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200 flex items-center gap-2"
                >
                  <Calendar className="h-4 w-4" />
                  Add Due Date
                </button>
              ) : (
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-sm font-medium text-gray-700">Due Date</label>
                    <button
                      onClick={() => {
                        setDueDateEnabled(false);
                        setDueDate('');
                      }}
                      className="text-xs text-red-600 hover:text-red-700 font-medium"
                    >
                      Remove Due Date
                    </button>
                  </div>
                  <input
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                rows="3"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes for the customer..."
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Terms */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Terms & Conditions
              </label>
              <textarea
                rows="2"
                value={terms}
                onChange={(e) => setTerms(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Send Options */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Send Invoice
              </label>
              <div className="space-y-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={sendViaEmail}
                    onChange={(e) => setSendViaEmail(e.target.checked)}
                    className="h-4 w-4 text-theme-blue focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Send via Email</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={sendViaSMS}
                    onChange={(e) => setSendViaSMS(e.target.checked)}
                    className="h-4 w-4 text-theme-blue focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Send via SMS</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={manualShare}
                    onChange={(e) => setManualShare(e.target.checked)}
                    className="h-4 w-4 text-theme-blue focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm text-gray-700">Manual Share</span>
                </label>
              </div>
              
              {/* Manual Share Info Message */}
              {manualShare && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-theme-blue mr-2 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Manual Share Selected</p>
                      <p>Invoice link will be generated and you should share the link with customer via email or SMS manually.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setSelectedCustomer(null);
                  setCustomerSearchTerm('');
                  setSearchedCustomers([]);
                  setLineItems([{ id: Date.now(), description: '', quantity: 1, unit_price: 0, amount: 0 }]);
                  setTaxRate(0);
                  setDueDate('');
                  setNotes('');
                  setSendViaEmail(false);
                  setSendViaSMS(false);
                  setManualShare(false);
                  setPaymentGateway('none');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Reset
              </button>
              <button
                onClick={handleGenerateInvoice}
                disabled={loading}
                style={{ backgroundColor: loading ? 'rgba(41, 173, 211, 0.6)' : 'var(--theme-primary)' }}
                className="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-all disabled:cursor-not-allowed flex items-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <FileText className="h-4 w-4 mr-2" />
                    Generate Invoice
                  </>
                )}
              </button>
            </div>
          </div>
        ) : activeTab === 'list' ? (
          // Invoice List Tab
          <div className="bg-white rounded-xl shadow-sm">
            {/* Search and Filter */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by invoice number, customer name, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    fetchInvoices();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="draft">Manual Send</option>
                  <option value="sent">Sent</option>
                  <option value="paid">Paid</option>
                  <option value="overdue">Overdue</option>
                </select>
              </div>
            </div>

            {/* Invoice Table */}
            <div className="overflow-x-auto">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue"></div>
                </div>
              ) : filteredInvoices.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500">No invoices found</p>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Invoice #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Due Date
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredInvoices.map((invoice) => (
                      <tr key={invoice._id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {businessInfo?.invoice_prefix || 'INV-'}{invoice.invoice_number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{invoice.customer_name}</div>
                          <div className="text-sm text-gray-500">{invoice.customer_email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          ${invoice.total?.toFixed(2)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(invoice.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(invoice.due_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => {
                                console.log('Viewing invoice:', invoice);
                                setViewingInvoice(invoice);
                                setShowInvoiceViewModal(true);
                              }}
                              className="text-theme-blue hover:text-blue-900"
                              title="View Invoice"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {invoice.manual_share_link && (
                              <button
                                onClick={() => {
                                  setShareInvoiceLink(invoice.manual_share_link);
                                  setShowShareLinkModal(true);
                                  setLinkCopied(false);
                                }}
                                className="text-theme-blue hover:text-blue-900"
                                title="Share Invoice Link"
                              >
                                <Link2 className="h-4 w-4" />
                              </button>
                            )}
                            {/* Resend button - only show for pending invoices */}
                            {invoice.payment_status === 'pending' && (
                              <button
                                onClick={() => {
                                  setInvoiceToResend(invoice);
                                  setShowResendModal(true);
                                }}
                                className="text-purple-600 hover:text-purple-900"
                                title="Resend Invoice"
                              >
                                <Send className="h-4 w-4" />
                              </button>
                            )}
                            {/* Delete button */}
                            <button
                              onClick={() => {
                                setInvoiceToDelete(invoice);
                                setShowDeleteModal(true);
                              }}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Invoice"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            
            {/* Pagination */}
            {!loading && invoices.length > 0 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalInvoices)}</span> of{' '}
                  <span className="font-medium">{totalInvoices}</span> invoices
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => fetchInvoices(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNum = index + 1;
                      // Show first page, last page, current page, and pages around current
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={pageNum}
                            onClick={() => fetchInvoices(pageNum)}
                            style={currentPage === pageNum ? { backgroundColor: 'var(--theme-primary)' } : {}}
                            className={`px-3 py-1 rounded-md text-sm font-medium ${
                              currentPage === pageNum
                                ? 'text-white'
                                : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      } else if (
                        pageNum === currentPage - 2 ||
                        pageNum === currentPage + 2
                      ) {
                        return <span key={pageNum} className="px-2 text-gray-500">...</span>;
                      }
                      return null;
                    })}
                  </div>
                  
                  <button
                    onClick={() => fetchInvoices(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : activeTab === 'settings' && user && (user.role === 'admin' || user.role === 'owner') ? (
          // Invoice Settings Tab - Only for Admin/Owner
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-6 space-y-6">
              {/* Invoice Prefix */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Prefix
                </label>
                <input
                  type="text"
                  value={invoicePrefix}
                  onChange={(e) => setInvoicePrefix(e.target.value)}
                  placeholder="INV-"
                  className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="mt-1 text-sm text-gray-500">
                  This prefix will be added before invoice numbers (e.g., INV-1000)
                </p>
              </div>

              {/* Invoice Starting Number */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Starting Number
                </label>
                <input
                  type="number"
                  value={invoiceStartingNumber}
                  onChange={(e) => setInvoiceStartingNumber(parseInt(e.target.value) || 1000)}
                  min="1"
                  className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="mt-1 text-sm text-gray-500">
                  New invoices will start from this number when counter is reset
                </p>
              </div>

              {/* Current Invoice Number */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Hash className="h-5 w-5 text-theme-blue" />
                      <h3 className="text-sm font-semibold text-gray-900">Current Invoice Number</h3>
                    </div>
                    <p className="text-2xl font-bold text-theme-blue">
                      {invoicePrefix}{invoiceCurrentNumber}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      Your next invoice will be: {invoicePrefix}{invoiceCurrentNumber + 1}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowResetModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
                  >
                    <RefreshCw className="h-4 w-4" />
                    Reset Counter
                  </button>
                </div>
              </div>

              {/* Bill From Template */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bill From Template
                </label>
                <p className="text-sm text-gray-600 mb-3">
                  Customize how your business information appears in the "Bill From" section of invoices.
                </p>
                
                <div className="grid grid-cols-3 gap-4">
                  {/* Template Textarea - Left side (2 columns) */}
                  <div className="col-span-2">
                    <textarea
                      value={billFromTemplate}
                      onChange={(e) => setBillFromTemplate(e.target.value)}
                      rows={8}
                      placeholder="{BUSINESS_NAME}&#10;{BUSINESS_STREET_ADDRESS}&#10;{BUSINESS_CITY_STATE_ZIP}&#10;{BUSINESS_PHONE}"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    />
                  </div>

                  {/* Available Placeholders - Right side (1 column) */}
                  <div className="col-span-1">
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase">Available Placeholders</h4>
                      <div className="space-y-1">
                        {placeholders.map((ph) => (
                          <button
                            key={ph.key}
                            onClick={() => insertPlaceholder(ph.key)}
                            className="w-full text-left px-2 py-1.5 text-xs bg-white hover:bg-blue-50 text-gray-700 hover:text-blue-700 rounded border border-gray-200 hover:border-blue-300 transition-colors"
                          >
                            {ph.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Default Payment Gateway */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Default Payment Gateway
                </label>
                <p className="text-sm text-gray-600 mb-3">
                  Select the payment gateway to use for all invoices. Only enabled gateways are shown.
                </p>
                <select
                  value={paymentGateway}
                  onChange={(e) => setPaymentGateway(e.target.value)}
                  className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="none">No Payment Gateway (Manual Share Only)</option>
                  {enabledGateways.map(gw => (
                    <option key={gw.gateway} value={gw.gateway}>
                      {gw.gateway.charAt(0).toUpperCase() + gw.gateway.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end">
              <button
                onClick={handleSaveSettings}
                disabled={saving}
                style={{ backgroundColor: saving ? 'rgba(41, 173, 211, 0.6)' : 'var(--theme-primary)' }}
                className="flex items-center gap-2 px-6 py-2.5 text-white rounded-lg hover:opacity-90 disabled:cursor-not-allowed transition-all font-medium"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </div>
        ) : null}
      </>
        )}

      {/* Invoice Creation Success Modal */}
      {showSuccessModal && createdInvoiceData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="bg-green-100 p-2 rounded-full">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Invoice Created Successfully!</h3>
                  <p className="text-sm text-gray-600">
                    Invoice #{businessInfo?.invoice_prefix || 'INV-'}{createdInvoiceData.invoice_number}
                  </p>
                </div>
              </div>
              <button
                onClick={closeSuccessModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Payment Link */}
              {createdInvoiceData.payment_link && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="h-5 w-5 text-theme-blue" />
                        <h4 className="text-sm font-semibold text-blue-900">Payment Link</h4>
                      </div>
                      <p className="text-xs text-blue-700 mb-3">
                        Share this link with your customer to receive payment via Stripe
                      </p>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={createdInvoiceData.payment_link}
                          readOnly
                          className="flex-1 px-3 py-2 text-sm bg-white border border-blue-300 rounded-md text-gray-700 truncate"
                        />
                        <button
                          onClick={() => copyToClipboard(createdInvoiceData.payment_link, 'Payment link')}
                          className="px-4 py-2 bg-theme-blue text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap"
                        >
                          Copy Link
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Manual Share Link */}
              {createdInvoiceData.manual_share_link && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="h-5 w-5 text-gray-600" />
                        <h4 className="text-sm font-semibold text-gray-900">Invoice View Link</h4>
                      </div>
                      <p className="text-xs text-gray-600 mb-3">
                        Share this link to let customers view the invoice (no payment processing)
                      </p>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={createdInvoiceData.manual_share_link}
                          readOnly
                          className="flex-1 px-3 py-2 text-sm bg-white border border-gray-300 rounded-md text-gray-700 truncate"
                        />
                        <button
                          onClick={() => copyToClipboard(createdInvoiceData.manual_share_link, 'Invoice link')}
                          className="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-md hover:bg-gray-700 whitespace-nowrap"
                        >
                          Copy Link
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Info message if no links */}
              {!createdInvoiceData.payment_link && !createdInvoiceData.manual_share_link && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                  <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">
                    Invoice has been created. You can view it in the "List Invoices" tab.
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closeSuccessModal}
                className="px-4 py-2 bg-theme-blue text-white rounded-md hover:bg-blue-700 font-medium"
              >
                View Invoices
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Validation Error Modal */}
      {showValidationModal && validationErrors.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="bg-red-100 p-2 rounded-full">
                  <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Validation Error</h3>
                  <p className="text-sm text-gray-600">Please correct the following errors:</p>
                </div>
              </div>
              <button
                onClick={closeValidationModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3 mb-6 max-h-96 overflow-y-auto">
              {validationErrors.map((error, index) => (
                <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-900">{error.field}</p>
                      <p className="text-sm text-red-700 mt-1">{error.message}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end">
              <button
                onClick={closeValidationModal}
                className="px-4 py-2 bg-theme-blue text-white rounded-md hover:bg-blue-700 font-medium"
              >
                Got it, I'll fix these
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Payment Link Modal */}
      {showPaymentLinkModal && (selectedPaymentLink || selectedInvoiceNumber) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="bg-green-100 p-2 rounded-full">
                  <DollarSign className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Invoice Links</h3>
                  <p className="text-sm text-gray-600">Invoice #{selectedInvoiceNumber}</p>
                </div>
              </div>
              <button
                onClick={closePaymentLinkModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Payment Link */}
              {selectedPaymentLink && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="h-5 w-5 text-theme-blue" />
                        <h4 className="text-sm font-semibold text-blue-900">Stripe Payment Link</h4>
                      </div>
                      <p className="text-xs text-blue-700 mb-3">
                        Share this link with your customer to receive payment via Stripe
                      </p>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={selectedPaymentLink}
                          readOnly
                          className="flex-1 px-3 py-2 text-sm bg-white border border-blue-300 rounded-md text-gray-700 truncate"
                        />
                        <button
                          onClick={() => copyToClipboard(selectedPaymentLink, 'Payment link')}
                          className="px-4 py-2 bg-theme-blue text-white text-sm font-medium rounded-md hover:bg-blue-700 whitespace-nowrap"
                        >
                          Copy Link
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={closePaymentLinkModal}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Share Invoice Link Modal - Compact */}
      {showShareLinkModal && shareInvoiceLink && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <div className="flex items-center gap-3">
                <div className="bg-white bg-opacity-20 p-2 rounded-full">
                  <Link2 className="h-5 w-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white">Share Invoice</h3>
              </div>
              <button
                onClick={() => {
                  setShowShareLinkModal(false);
                  setLinkCopied(false);
                }}
                className="text-white hover:text-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              <div className="flex items-center gap-2">
              <input
                type="text"
                value={shareInvoiceLink}
                readOnly
                className="flex-1 px-3 py-2 text-sm bg-gray-50 border border-gray-300 rounded-md text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={(e) => e.target.select()}
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(shareInvoiceLink);
                  setLinkCopied(true);
                  setTimeout(() => setLinkCopied(false), 2000);
                }}
                className={`px-4 py-2 ${linkCopied ? 'bg-green-600' : 'bg-theme-blue'} text-white text-sm font-medium rounded-md hover:opacity-90 whitespace-nowrap flex items-center gap-2 transition-colors`}
              >
                {linkCopied ? (
                  <>
                    <Check className="h-4 w-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4" />
                    Copy
                  </>
                )}
              </button>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowShareLinkModal(false);
                    setLinkCopied(false);
                  }}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Public Invoice View Modal */}
      {showInvoiceViewModal && viewingInvoice && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeInvoiceViewModal}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-4xl w-full flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Sticky Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 rounded-t-lg px-6 py-4 flex items-center justify-between">
              <div className="flex-1">
                <h2 className="text-xl font-bold text-gray-900">{viewingInvoice.customer_name}</h2>
                <p className="text-sm text-gray-500">
                  Invoice #{businessInfo?.invoice_prefix || 'INV-'}{viewingInvoice.invoice_number}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handlePrintInvoice}
                  className="text-gray-600 hover:text-gray-900 transition-colors"
                  title="Print Invoice"
                >
                  <Printer className="h-5 w-5" />
                </button>
                <button
                  onClick={closeInvoiceViewModal}
                  className="text-gray-400 hover:text-gray-600"
                  title="Close"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto px-6 py-6 space-y-6">
              {/* Bill From and Bill To Section */}
              <div className="grid grid-cols-2 gap-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                {/* Bill From */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Bill From:</h3>
                  <div className="text-sm text-gray-600 whitespace-pre-line">
                    {renderBillFrom()}
                  </div>
                </div>

                {/* Bill To */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Bill To:</h3>
                  <div className="text-sm space-y-1">
                    <p className="font-semibold text-gray-900">{viewingInvoice.customer_name}</p>
                    {viewingInvoice.customer_email && (
                      <p className="text-gray-600 flex items-center gap-1">
                        <Mail className="h-3 w-3" />
                        {viewingInvoice.customer_email}
                      </p>
                    )}
                    {viewingInvoice.customer_phone && (
                      <p className="text-gray-600 flex items-center gap-1">
                        <Phone className="h-3 w-3" />
                        {viewingInvoice.customer_phone}
                      </p>
                    )}
                    {viewingInvoice.customer_address && (
                      <p className="text-gray-600 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {viewingInvoice.customer_address}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Invoice Date and Due Date Section */}
              <div className="grid grid-cols-2 gap-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Invoice Date</p>
                  <p className="text-sm text-gray-900 mt-1">
                    {new Date(viewingInvoice.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase">Due Date</p>
                  <p className="text-sm text-gray-900 mt-1">
                    {new Date(viewingInvoice.due_date).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* Order Details Section */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700">Order Details</h3>
                </div>
                <div className="p-4">
                  {/* Items Table */}
                  <table className="min-w-full">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Description</th>
                        <th className="px-3 py-2 text-center text-xs font-medium text-gray-600 uppercase">Qty</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase">Price</th>
                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase">Amount</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {viewingInvoice.line_items && viewingInvoice.line_items.map((item, index) => (
                        <tr key={index}>
                          <td className="px-3 py-3 text-sm text-gray-900">{item.description}</td>
                          <td className="px-3 py-3 text-sm text-gray-600 text-center">{item.quantity}</td>
                          <td className="px-3 py-3 text-sm text-gray-600 text-right">${item.unit_price.toFixed(2)}</td>
                          <td className="px-3 py-3 text-sm font-medium text-gray-900 text-right">${item.amount.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Totals */}
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="text-right font-medium text-gray-900">${viewingInvoice.subtotal?.toFixed(2) || '0.00'}</span>
                    </div>
                    {viewingInvoice.tax_rate > 0 && (
                      <div className="grid grid-cols-2 gap-2 text-sm mt-2">
                        <span className="text-gray-600">Tax ({viewingInvoice.tax_rate}%)</span>
                        <span className="text-right font-medium text-gray-900">${viewingInvoice.tax_amount?.toFixed(2) || '0.00'}</span>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2 pt-2 mt-2 border-t border-gray-300">
                      <span className="text-sm font-semibold text-gray-900">Total</span>
                      <span className="text-right text-sm font-semibold text-gray-900">${viewingInvoice.total?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Terms Section */}
              {viewingInvoice.terms && (
                <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Terms & Conditions</h3>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{viewingInvoice.terms}</p>
                </div>
              )}

              {/* Notes Section */}
              {viewingInvoice.notes && (
                <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Notes</h3>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{viewingInvoice.notes}</p>
                </div>
              )}

              {/* Payment Status Section */}
              <div className="p-4 border border-gray-200 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Payment Status</h3>
                {viewingInvoice.payment_status === 'completed' ? (
                  <div className="flex items-center gap-2 bg-green-50 p-3 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <div>
                      <p className="text-sm font-semibold text-green-900">Payment Complete</p>
                      <p className="text-xs text-green-700">
                        Paid on {viewingInvoice.paid_at ? new Date(viewingInvoice.paid_at).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 bg-orange-50 p-3 rounded-lg">
                    <Clock className="h-5 w-5 text-orange-600" />
                    <div>
                      <p className="text-sm font-semibold text-orange-900">Payment Pending</p>
                      <p className="text-xs text-orange-700">Awaiting payment</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sticky Footer */}
            {viewingInvoice.payment_status !== 'completed' && viewingInvoice.stripe_client_secret && (
              <div className="sticky bottom-0 bg-white border-t border-gray-200 rounded-b-lg px-6 py-4">
                <button
                  onClick={() => openPaymentModal(viewingInvoice)}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 text-white rounded-lg hover:opacity-90 font-semibold text-base"
                  style={{ backgroundColor: 'var(--theme-primary)' }}
                >
                  <DollarSign className="h-5 w-5" />
                  Pay Now - ${viewingInvoice.total?.toFixed(2)}
                </button>
              </div>
            )}

            {/* Paid - Disabled Button */}
            {viewingInvoice.payment_status === 'completed' && (
              <div className="sticky bottom-0 bg-white border-t border-gray-200 rounded-b-lg px-6 py-4">
                <button
                  disabled
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-gray-300 text-gray-500 rounded-lg font-semibold text-base cursor-not-allowed"
                >
                  <CheckCircle className="h-5 w-5" />
                  Payment Complete
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Stripe Inline Payment Modal */}
      {showPaymentModal && paymentClientSecret && stripeReady && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <div>
                <h3 className="text-lg font-semibold text-white">Complete Payment</h3>
              </div>
              <button
                onClick={closePaymentModal}
                className="text-white hover:text-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6">
              <Elements stripe={stripeReady}>
              <PaymentForm
                clientSecret={paymentClientSecret}
                amount={paymentAmount}
                invoiceId={paymentInvoiceId}
                onSuccess={handlePaymentSuccess}
                onCancel={closePaymentModal}
              />
              </Elements>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center">
                  🔒 Secure payment powered by Stripe
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Confirmation Modal */}
      {showPaymentConfirmation && paidInvoiceDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <div className="flex items-center gap-3">
                <div className="bg-white bg-opacity-20 p-2 rounded-full">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white">Payment Successful!</h3>
              </div>
            </div>

            <div className="p-6">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <CheckCircle className="h-10 w-10 text-green-600" />
                </div>
                <p className="text-gray-600 mb-2">Your payment has been processed successfully.</p>
                <p className="text-2xl font-bold text-gray-900">
                  ${paidInvoiceDetails.total?.toFixed(2)}
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Invoice Number:</span>
                  <span className="font-medium text-gray-900">
                    {businessInfo?.invoice_prefix || 'INV-'}{paidInvoiceDetails.invoice_number}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Customer:</span>
                  <span className="font-medium text-gray-900">{paidInvoiceDetails.customer_name}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Payment Date:</span>
                  <span className="font-medium text-gray-900">
                    {new Date().toLocaleDateString()}
                  </span>
                </div>
              </div>

              <p className="text-xs text-gray-500 text-center mb-4">
                A receipt has been sent to your email address.
              </p>

              <button
                onClick={() => {
                  setShowPaymentConfirmation(false);
                  setPaidInvoiceDetails(null);
                  navigate('/');
                }}
                className="w-full px-4 py-3 text-white rounded-md hover:opacity-90 font-semibold"
                style={{ backgroundColor: 'var(--theme-primary)' }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Validation Error Modal */}
      {showValidationError && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <div className="flex items-center gap-3">
                <div className="bg-white bg-opacity-20 p-2 rounded-full">
                  <AlertCircle className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white">Validation Error</h3>
              </div>
            </div>

            <div className="p-6">
              <div className="flex items-start gap-3 mb-6">
                <div className="flex-shrink-0 mt-0.5">
                  <AlertCircle className="h-5 w-5 text-orange-500" />
                </div>
                <p className="text-gray-700 text-sm leading-relaxed">
                  {validationErrorMessage}
                </p>
              </div>

              <button
                onClick={() => {
                  setShowValidationError(false);
                  setValidationErrorMessage('');
                }}
                className="w-full px-4 py-3 text-white rounded-md hover:opacity-90 font-semibold"
                style={{ backgroundColor: 'var(--theme-primary)' }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Invoice Counter Confirmation Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-orange-100 p-2 rounded-full">
                <AlertCircle className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Reset Invoice Counter?</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              This will reset the invoice counter to <strong>{invoiceStartingNumber}</strong>. 
              Your next invoice will be <strong>{invoicePrefix}{invoiceStartingNumber}</strong>.
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowResetModal(false)}
                disabled={resetting}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleResetCounter}
                disabled={resetting}
                className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-orange-400 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {resetting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Resetting...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    Reset Counter
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Delete Invoice Confirmation Modal */}
      {showDeleteModal && invoiceToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-red-100 p-2 rounded-full">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Invoice?</h3>
            </div>
            
            <p className="text-gray-600 mb-2">
              Are you sure you want to delete invoice <strong>#{businessInfo?.invoice_prefix || 'INV-'}{invoiceToDelete.invoice_number}</strong>?
            </p>
            <p className="text-sm text-gray-500 mb-6">
              This action cannot be undone.
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setInvoiceToDelete(null);
                }}
                disabled={deleting}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteInvoice}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Delete Invoice
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resend Invoice Modal */}
      {showResendModal && invoiceToResend && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-purple-100 p-2 rounded-full">
                <Send className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Resend Invoice</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              How would you like to resend invoice <strong>#{businessInfo?.invoice_prefix || 'INV-'}{invoiceToResend.invoice_number}</strong>?
            </p>
            
            <div className="flex flex-col gap-3">
              <button
                onClick={() => handleResendInvoice('email')}
                disabled={resending}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-theme-blue text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {resending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                ) : (
                  <>
                    <Mail className="h-5 w-5" />
                    Send via Email
                  </>
                )}
              </button>
              
              <button
                onClick={() => handleResendInvoice('sms')}
                disabled={resending}
                className="flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed transition-colors font-medium"
              >
                {resending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                ) : (
                  <>
                    <Phone className="h-5 w-5" />
                    Send via SMS
                  </>
                )}
              </button>
              
              <button
                onClick={() => {
                  setShowResendModal(false);
                  setInvoiceToResend(null);
                }}
                disabled={resending}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}


      </div>
    </div>
  );
};

export default Invoices;
